import React, { useState } from 'react';

export type BarSeries = {
    label: string;
    color: string;
    data: { category: string; count: number }[];
};

type HBarChartProps = {
    title: string;
    series: BarSeries[];
    className?: string;
    style?: React.CSSProperties;
};

const HBarChart = ({ title, series, className, style }: HBarChartProps) => {
    const [hoveredBar, setHoveredBar] = useState<{ seriesIdx: number; catIdx: number } | null>(null);

    if (!series.length || !series[0].data.length) return null;

    // Collect all categories from first series
    const categories = series[0].data.map(d => d.category);
    const maxValue = Math.max(...series.flatMap(s => s.data.map(d => d.count)), 1);

    const barHeight = 22;
    const groupGap = 10;
    const labelWidth = 120;
    const chartPadding = { top: 8, right: 16, bottom: 4, left: labelWidth + 8 };

    const groupHeight = series.length * barHeight + (series.length - 1) * 2;
    const totalHeight = categories.length * (groupHeight + groupGap) - groupGap + chartPadding.top + chartPadding.bottom;

    // Grid lines
    const gridLines = [0, 0.25, 0.5, 0.75, 1];

    return (
        <div className={className} style={{ display: 'flex', flexDirection: 'column', gap: 12, ...style }}>
            <div className="flex items-center justify-between">
                <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{title}</span>
                {series.length > 1 && (
                    <div className="flex items-center gap-4">
                        {series.map((s, i) => (
                            <div key={i} className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>
                                <span className="rounded" style={{ width: 10, height: 10, backgroundColor: s.color }} />
                                {s.label}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div style={{ position: 'relative', width: '100%', minHeight: Math.min(totalHeight, 400), maxHeight: 500, overflowY: 'auto', overflowX: 'hidden' }}>
                <svg width="100%" height={totalHeight} style={{ display: 'block' }}>
                    {/* Grid lines — positions are relative to bar area: left + frac * (100% - left - right) */}
                    {gridLines.map((frac, i) => {
                        // calc(left*(1-frac) - right*frac + frac*100%) == left + frac*(100%-left-right)
                        const xCalc = `calc(${chartPadding.left * (1 - frac) - chartPadding.right * frac}px + ${frac * 100}%)`;
                        return (
                            <g key={i}>
                                <line
                                    x1={xCalc}
                                    y1={chartPadding.top}
                                    x2={xCalc}
                                    y2={totalHeight - chartPadding.bottom}
                                    stroke="var(--border-default)"
                                    strokeDasharray={i === 0 ? 'none' : '3 3'}
                                />
                            </g>
                        );
                    })}

                    {/* Bars */}
                    {categories.map((cat, catIdx) => {
                        const groupY = chartPadding.top + catIdx * (groupHeight + groupGap);
                        const truncatedLabel = cat.length > 16 ? cat.slice(0, 15) + '…' : cat;

                        return (
                            <g key={catIdx}>
                                {/* Category label */}
                                <text
                                    x={chartPadding.left - 8}
                                    y={groupY + groupHeight / 2}
                                    textAnchor="end"
                                    dominantBaseline="central"
                                    fill="var(--text-secondary)"
                                    fontSize={11}
                                    fontFamily="'Inter', sans-serif"
                                >
                                    <title>{cat}</title>
                                    {truncatedLabel}
                                </text>

                                {/* Series bars */}
                                {series.map((s, sIdx) => {
                                    const datum = s.data.find(d => d.category === cat);
                                    const val = datum?.count ?? 0;
                                    const barY = groupY + sIdx * (barHeight + 2);
                                    const isHovered = hoveredBar?.seriesIdx === sIdx && hoveredBar?.catIdx === catIdx;

                                    return (
                                        <g
                                            key={sIdx}
                                            onMouseEnter={() => setHoveredBar({ seriesIdx: sIdx, catIdx })}
                                            onMouseLeave={() => setHoveredBar(null)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {/* Background track */}
                                            <rect
                                                x={chartPadding.left}
                                                y={barY}
                                                width={`calc(100% - ${chartPadding.left + chartPadding.right}px)`}
                                                height={barHeight}
                                                rx={4}
                                                fill="var(--surface-100)"
                                            />
                                            {/* Value bar — width is fraction of available area (100% - left - right) */}
                                            <rect
                                                x={chartPadding.left}
                                                y={barY}
                                                width={`calc(${(val / maxValue) * 100}% - ${(val / maxValue) * (chartPadding.left + chartPadding.right)}px)`}
                                                height={barHeight}
                                                rx={4}
                                                fill={s.color}
                                                opacity={isHovered ? 1 : 0.8}
                                                style={{
                                                    transition: 'opacity 0.15s ease',
                                                    transform: `scaleX(1)`,
                                                    transformOrigin: `${chartPadding.left}px ${barY}px`,
                                                }}
                                            />
                                            {/* Value label */}
                                            {val > 0 && (
                                                <text
                                                    x={chartPadding.left + 8}
                                                    y={barY + barHeight / 2}
                                                    dominantBaseline="central"
                                                    fill="white"
                                                    fontSize={11}
                                                    fontWeight={600}
                                                    fontFamily="'Inter', sans-serif"
                                                >
                                                    {val}
                                                </text>
                                            )}
                                        </g>
                                    );
                                })}
                            </g>
                        );
                    })}
                </svg>
            </div>
        </div>
    );
};

export default HBarChart;
